/**
 * React Query hooks for subscription data
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { KeycloakService } from '../services/keycloak.service';
import { SubscriptionService } from '../services/subscription.service';
import type { UsageStats, Plan } from '../services/subscription.service';

/**
 * Fetch current usage statistics (credits, plan info)
 */
export function useUsageStats() {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();

  return useQuery<UsageStats | null, Error>({
    queryKey: ['subscription', 'usage'],
    queryFn: async () => {
      if (!KeycloakService.isAuthenticated()) {
        return null;
      }
      return SubscriptionService.getUsageStats();
    },
    enabled: isAuthenticated && !isAuthLoading,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Fetch all available subscription plans
 */
export function usePlans() {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();

  return useQuery<Plan[] | null, Error>({
    queryKey: ['subscription', 'plans'],
    queryFn: async () => {
      if (!KeycloakService.isAuthenticated()) {
        return null;
      }
      return SubscriptionService.getPlans();
    },
    enabled: isAuthenticated && !isAuthLoading,
    staleTime: 10 * 60 * 1000,
  });
}
